import { useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import type { TreeMenuItem, useLink } from '@refinedev/core';
import { IERPUser } from '@admin-panel/types';
import { groupTopLevel } from './helpers';
import TopLevelItems from './TopLevelItems';
import PanelContent from './PanelContent';
import AvatarPopover from './AvatarPopover';
import { AnimatePresence } from 'motion/react';
import * as Styles from './styles';

type LinkComponent = ReturnType<typeof useLink>;

interface IDesktopSiderProps {
  menuItems: Array<TreeMenuItem>;
  selectedKey?: string | null;
  Link: LinkComponent;
  user?: IERPUser;
  onLogout: () => void;
}

const DesktopSider = ({ menuItems, selectedKey, Link, user, onLogout }: IDesktopSiderProps) => {
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const hideTimer = useRef<number | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const topLevel = useMemo(() => groupTopLevel(menuItems), [menuItems]);
  const hoveredItem = useMemo(() => topLevel.find(i => i.key === hoverKey), [topLevel, hoverKey]);

  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'right-start',
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      {
        name: 'preventOverflow',
        options: {
          boundary: 'clippingParents',
          rootBoundary: 'viewport',
          padding: 12,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['right-end', 'left-start', 'left-end'],
        },
      },
      { name: 'computeStyles', options: { gpuAcceleration: false } },
    ],
  });

  const handleEnter = (el: HTMLDivElement, key: string) => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    setReferenceElement(el);
    setHoverKey(key);
  };
  const handleLeave = (key: string) => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      setHoverKey(prev => (prev === key ? null : prev));
    }, 180);
  };

  return (
    <>
      <Styles.Container>
        <Styles.NavScrollArea>
          <TopLevelItems
            items={topLevel}
            selectedKey={selectedKey}
            Link={Link}
            onItemEnter={handleEnter}
            onItemLeave={handleLeave}
          />
        </Styles.NavScrollArea>
        <Styles.BottomBar>
          <AvatarPopover user={user} onLogout={onLogout} />
        </Styles.BottomBar>
      </Styles.Container>

      <AnimatePresence>
        {hoveredItem && hoveredItem.children && hoveredItem.children.length > 0 && (
          <Styles.HoverPanel
            ref={setPopperElement}
            initial={{ x: -12, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -12, opacity: 0 }}
            transition={{ type: 'tween', duration: 0.18 }}
            style={popperStyles.popper}
            {...attributes.popper}
            onMouseEnter={() => {
              if (hideTimer.current) window.clearTimeout(hideTimer.current);
              setHoverKey(hoveredItem.key);
            }}
            onMouseLeave={() => {
              if (hideTimer.current) window.clearTimeout(hideTimer.current);
              hideTimer.current = window.setTimeout(() => setHoverKey(null), 180);
            }}
          >
            <PanelContent item={hoveredItem} selectedKey={selectedKey} Link={Link} />
          </Styles.HoverPanel>
        )}
      </AnimatePresence>
    </>
  );
};

export default DesktopSider;
